'use memo';

import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import VStack from '@/components/Layout/VStack';
import { Loader } from '@/components/Loading';
import { Text } from '@/components/Typography';
import { AuthMethodTabs } from '@/features/auth/components/AuthMethodTabs';
import { useAuthMethods } from '@/features/auth/hooks/useAuthMethods';
import { useReauthFlow } from '@/features/auth/hooks/useReauthFlow';
import { useAuthStore } from '@/lib/auth';
import { authService } from '@/lib/auth/ory/service';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { AdditionalAuthGate } from '../AdditionalAuth';

/**
 * 認証要件（何を求めるか）
 * - "mfa": MFA認証（AAL2）を要求
 * - "password": パスワード再入力を要求（セッションのリフレッシュ）
 * - "mfa_or_password": MFA有効ならMFA、無効ならパスワード再入力を要求
 */
type AuthRequirement = 'mfa' | 'password' | 'mfa_or_password';

/**
 * 強制モード（いつ求めるか）
 * - "always": 常に指定の認証を要求
 * - "if_configured": ユーザー設定に応じて要求（MFAの場合はMFA有効時のみ）
 */
type EnforcementMode = 'always' | 'if_configured';

type SecureGuardProps = {
  /**
   * 認証後に表示するコンテンツ
   */
  children: ReactNode;
  /**
   * 認証要件（何を求めるか）
   * - "mfa": MFA認証（AAL2）を要求
   * - "password": パスワード再入力を要求
   * - "mfa_or_password": MFA有効ならMFA、無効ならパスワード再入力を要求
   * @default "mfa"
   */
  requirement?: AuthRequirement;
  /**
   * 強制モード（いつ求めるか）
   * - "always": 常に指定の認証を要求
   * - "if_configured": ユーザー設定に応じて要求
   * @default "if_configured"
   */
  enforcement?: EnforcementMode;
  /**
   * 検証中に表示するフォールバックコンテンツ
   * 未指定の場合はデフォルトのローディングUIを表示
   */
  fallback?: ReactNode;
  /**
   * 認証キャンセル時のコールバック
   * 未指定の場合はキャンセルボタンを非表示
   */
  onCancel?: () => void;
  /**
   * 認証成功時のコールバック
   */
  onAuthSuccess?: () => void;
};

type GuardState = 'loading' | 'needs_mfa' | 'needs_password' | 'authenticated';

/**
 * SecureGuard
 *
 * 追加認証を要求するガードコンポーネントです。
 * このコンポーネントで囲まれた領域は、追加認証が完了するまでアクセスできません。
 *
 * 2つの軸で認証要件を指定できます:
 * - `requirement`: 何を求めるか（"mfa" | "password" | "mfa_or_password"）
 * - `enforcement`: いつ求めるか（"always" | "if_configured"）
 *
 * @example
 * ```tsx
 * // MFAが有効な場合のみMFA認証を求める
 * <SecureGuard requirement="mfa" enforcement="if_configured">
 *   <ProtectedContent />
 * </SecureGuard>
 *
 * // 常にMFA認証を求める（MFA設定変更など）
 * <SecureGuard requirement="mfa" enforcement="always">
 *   <MfaSettings />
 * </SecureGuard>
 *
 * // 常にパスワード再入力を求める（重要な操作前の本人確認）
 * <SecureGuard requirement="password" enforcement="always">
 *   <DangerousSettings />
 * </SecureGuard>
 *
 * // MFA有効ならMFA、無効ならパスワード再入力（パスワード変更など）
 * <SecureGuard requirement="mfa_or_password" enforcement="if_configured">
 *   <ChangePasswordForm />
 * </SecureGuard>
 *
 * // キャンセルボタン付き
 * <SecureGuard requirement="mfa" enforcement="always" onCancel={() => navigate({ to: '/home' })}>
 *   <SensitiveSettings />
 * </SecureGuard>
 * ```
 */
export function SecureGuard({
  children,
  requirement = 'mfa',
  enforcement = 'if_configured',
  fallback,
  onCancel,
  onAuthSuccess,
}: SecureGuardProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth.secureGuard',
  });
  const { downloadSignedCookies, refreshUserSession } = useAuthStore();
  const [guardState, setGuardState] = useState<GuardState>('loading');

  // 認証状態をチェック
  const checkAuthStatus = useCallback(async () => {
    setGuardState('loading');

    try {
      // パスワード再入力のみが必要な場合
      if (requirement === 'password') {
        if (enforcement === 'always') {
          setGuardState('needs_password');
        } else {
          // if_configured でパスワードは常に認証済み扱い
          setGuardState('authenticated');
        }
        return;
      }

      // MFA または MFA_or_password の場合
      // AAL1でセッションを取得して、現在の状態を確認
      const result = await authService.getSession('aal1');

      if (result.error || !result.data) {
        // セッションが取得できない場合
        if (enforcement === 'always') {
          setGuardState(
            requirement === 'mfa_or_password' ? 'needs_password' : 'needs_mfa',
          );
        } else {
          setGuardState('authenticated');
        }
        return;
      }

      const session = result.data;

      // enforcement="always" の場合は、セッション状態に関わらず常に認証を求める
      if (enforcement === 'always') {
        // mfa_or_password の場合、MFA有効ならMFA、無効ならパスワード
        if (requirement === 'mfa_or_password') {
          setGuardState(session.isMfaEnabled ? 'needs_mfa' : 'needs_password');
        } else {
          setGuardState('needs_mfa');
        }
        return;
      }

      // enforcement="if_configured" の場合
      // AAL2まで認証済みの場合は認証不要
      if (session.isAal2Authenticated) {
        setGuardState('authenticated');
        return;
      }

      // MFAが有効でAAL2未認証の場合はMFA認証を求める
      if (session.isMfaEnabled) {
        setGuardState('needs_mfa');
        return;
      }

      // MFAが無効の場合
      if (requirement === 'mfa_or_password') {
        // mfa_or_password の場合はパスワード再入力を求める
        setGuardState('needs_password');
      } else {
        // mfa の場合は認証済みとして扱う
        setGuardState('authenticated');
      }
    } catch (error) {
      console.error('Failed to check auth status:', error);
      // エラー時は安全側に倒し、常に認証を要求する（フェイルクローズ）
      // 認証状態を確認できない場合は認証済みとはみなさない
      setGuardState(
        requirement === 'password' || requirement === 'mfa_or_password'
          ? 'needs_password'
          : 'needs_mfa',
      );
    }
  }, [requirement, enforcement]);

  // マウント時に認証ステータスをチェック
  useEffect(() => {
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
    void checkAuthStatus();
  }, [checkAuthStatus]);

  // MFA認証成功時のハンドラー
  const handleMfaAuthSuccess = useCallback(async () => {
    try {
      await downloadSignedCookies();
      await refreshUserSession();
    } catch (error) {
      console.error('Failed to refresh session after MFA auth:', error);
    }

    setGuardState('authenticated');
    onAuthSuccess?.();
  }, [downloadSignedCookies, refreshUserSession, onAuthSuccess]);

  // パスワード認証成功時のハンドラー
  const handlePasswordAuthSuccess = useCallback(async () => {
    try {
      await downloadSignedCookies();
      await refreshUserSession();
    } catch (error) {
      console.error('Failed to refresh session after password auth:', error);
    }

    setGuardState('authenticated');
    onAuthSuccess?.();
  }, [downloadSignedCookies, refreshUserSession, onAuthSuccess]);

  // ローディング中
  if (guardState === 'loading') {
    return (
      fallback ?? (
        <div className="flex justify-center items-center py-12">
          <Loader variant="current" size="size-8" />
        </div>
      )
    );
  }

  // 認証済み
  if (guardState === 'authenticated') {
    return <>{children}</>;
  }

  // MFA認証が必要
  if (guardState === 'needs_mfa') {
    return (
      <VStack className="gap-6 max-w-sm mx-auto">
        <VStack className="items-center gap-3 text-center">
          <Icon
            iconName="ShieldCheckIcon"
            className="text-primary-on-neutral-fill"
            size={48}
          />
          <Text variant="CardHeader">{t('title')}</Text>
          <Text variant="p" className="text-neutral-muted-on-fill">
            {t('description')}
          </Text>
        </VStack>

        <VStack className="gap-5 w-full">
          <AdditionalAuthGate onAuthSuccess={handleMfaAuthSuccess} autoInit />

          {onCancel && (
            <div className="flex justify-center">
              <ActionButton variant="ghost" onClick={onCancel}>
                {t('cancel')}
              </ActionButton>
            </div>
          )}
        </VStack>
      </VStack>
    );
  }

  // パスワード認証が必要
  return (
    <PasswordAuthContent
      onAuthSuccess={handlePasswordAuthSuccess}
      onCancel={onCancel}
    />
  );
}

/**
 * パスワード認証コンポーネント
 */
function PasswordAuthContent({
  onAuthSuccess,
  onCancel,
}: {
  onAuthSuccess: () => Promise<void>;
  onCancel?: () => void;
}) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth.secureGuard',
  });
  const { t: tReauth } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth.reauth',
  });

  const { flow, isPending, isInitializing, onSubmit, initFlow } = useReauthFlow(
    {
      enabled: true,
      reauthType: 'refresh',
      onSuccess: onAuthSuccess,
    },
  );

  const authMethods = useAuthMethods(flow?.ui ?? { nodes: [], messages: [] });

  // マウント時にフローを初期化
  useEffect(() => {
    if (!flow && !isInitializing) {
      void initFlow();
    }
  }, [flow, isInitializing, initFlow]);

  // ローディング中
  if (!flow || isInitializing) {
    return (
      <VStack className="gap-6 max-w-sm mx-auto">
        <VStack className="items-center gap-3 text-center">
          <Icon
            iconName="KeyRoundIcon"
            className="text-primary-on-neutral-fill animate-pulse"
            size={48}
          />
          <Text variant="CardHeader">{t('passwordTitle')}</Text>
          <Text variant="p" className="text-neutral-muted-on-fill">
            {tReauth('description')}
          </Text>
        </VStack>
        <div className="flex justify-center py-6">
          <Loader variant="current" size="size-8" />
        </div>
      </VStack>
    );
  }

  return (
    <VStack className="gap-6 max-w-sm mx-auto">
      <VStack className="items-center gap-3 text-center">
        <Icon
          iconName="KeyRoundIcon"
          className="text-primary-on-neutral-fill"
          size={48}
        />
        <Text variant="CardHeader">{t('passwordTitle')}</Text>
        <Text variant="p" className="text-neutral-muted-on-fill">
          {tReauth('description')}
        </Text>
      </VStack>

      <VStack className="gap-5 w-full">
        <AuthMethodTabs
          methods={authMethods}
          disabled={isPending}
          onSubmit={onSubmit}
        />

        {onCancel && (
          <div className="flex justify-center">
            <ActionButton variant="ghost" onClick={onCancel}>
              {t('cancel')}
            </ActionButton>
          </div>
        )}
      </VStack>
    </VStack>
  );
}
